import { createSlice } from '@reduxjs/toolkit'
import { register } from '../../../../../helpers/reducerRegistry'
import { getDataTable, getIds } from '../helper'
import {
  getActivitiesRatioAndRisk,
  getActivitiesRatioAndRiskCompare,
  getCommentary,
  getDistributeScoreChart,
  getDistributeScoreCompareChart,
  getGenericRatioHighlight,
  getIndustryRatioDetail,
  getPerformanMatrixChartData,
  getPerformanMatrixDisplayData,
} from './thunk'

const slice = createSlice({
  name: 'industryAnalysis/generalAnalysis',
  initialState: {
    isLoading: {
      genericRatioHighlight: false,
      financialRatios: false,
      activitiesRatiosAndRisk: false,
      activitiesRatiosAndRiskCompare: false,
      distributionScore: false,
      distributionScoreCompare: false,
      commentary: false,
      matrixChart: false,
      matrixTooltip: false,
    },
    genericRatioHighlightData: {},
    financialRatiosIds: [],
    financialRatiosData: {},
    activitiesRatiosAndRiskIds: [],
    activitiesRatiosAndRiskData: {},
    distributionScoreData: [],
    distributionScoreDataCompare: [],
    commentaryData: [],
    matrixChart: {},
    matrixTooltip: {},
  },
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getGenericRatioHighlight.pending, (state) => {
      state.isLoading.genericRatioHighlight = true
    })
    builder.addCase(getGenericRatioHighlight.fulfilled, (state, action) => {
      state.genericRatioHighlightData = action.payload
      state.isLoading.genericRatioHighlight = false
    })
    builder.addCase(getGenericRatioHighlight.rejected, (state, action) => {
      state.isLoading.genericRatioHighlight = action.payload.loading
    })

    builder.addCase(getIndustryRatioDetail.pending, (state) => {
      state.financialRatiosIds = []
      state.financialRatiosData = {}
      state.isLoading.financialRatios = true
    })
    builder.addCase(getIndustryRatioDetail.fulfilled, (state, { payload }) => {
      const ids = getIds(payload)
      if (ids.length) {
        state.financialRatiosIds = ids
        state.financialRatiosData = getDataTable(payload, ids)
      }
      state.isLoading.financialRatios = false
    })
    builder.addCase(getIndustryRatioDetail.rejected, (state, action) => {
      state.isLoading.financialRatios = action.payload.loading
    })

    builder.addCase(getActivitiesRatioAndRisk.pending, (state) => {
      state.activitiesRatiosAndRiskIds = []
      state.activitiesRatiosAndRiskData = {}
      state.isLoading.activitiesRatiosAndRisk = true
    })
    builder.addCase(
      getActivitiesRatioAndRisk.fulfilled,
      (state, { payload }) => {
        const ids = getIds(payload || [])
        if (ids.length) {
          state.activitiesRatiosAndRiskIds = [
            ...new Set([...state.activitiesRatiosAndRiskIds, ...ids]),
          ]
          const newData = getDataTable(payload, ids)

          Object.keys(newData).forEach((key) => {
            const currentData = state.activitiesRatiosAndRiskData[key] || []
            state.activitiesRatiosAndRiskData[key] = mergeGroupData(
              currentData,
              newData[key],
            )
          })
        }
        state.isLoading.activitiesRatiosAndRisk = false
      },
    )
    builder.addCase(getActivitiesRatioAndRisk.rejected, (state, action) => {
      state.isLoading.activitiesRatiosAndRisk = action.payload.loading
    })

    builder.addCase(getActivitiesRatioAndRiskCompare.pending, (state) => {
      state.activitiesRatiosAndRiskIds = []
      state.activitiesRatiosAndRiskData = {}
      state.isLoading.activitiesRatiosAndRiskCompare = true
    })
    builder.addCase(
      getActivitiesRatioAndRiskCompare.fulfilled,
      (state, { payload }) => {
        const data = payload.map((item) => ({
          compareIndustry: item.defaultIndustry,
          comparingIndustryChange: item.defaultIndustryChange,
          riskLevelCompare: item.riskLevel,
          group: item.group,
          ratioName: item.ratioName,
        }))
        const ids = getIds(data || [])
        if (ids.length) {
          state.activitiesRatiosAndRiskIds = [
            ...new Set([...state.activitiesRatiosAndRiskIds, ...ids]),
          ]
          const newData = getDataTable(data, ids)

          Object.keys(newData).forEach((key) => {
            const currentData = state.activitiesRatiosAndRiskData[key] || []
            state.activitiesRatiosAndRiskData[key] = mergeGroupData(
              currentData,
              newData[key],
            )
          })
        }
        state.isLoading.activitiesRatiosAndRiskCompare = false
      },
    )
    builder.addCase(
      getActivitiesRatioAndRiskCompare.rejected,
      (state, action) => {
        state.isLoading.activitiesRatiosAndRiskCompare = action.payload.loading
      },
    )

    builder.addCase(getDistributeScoreChart.pending, (state) => {
      state.distributionScoreData = []
      state.distributionScoreDataCompare = []
      state.isLoading.distributionScore = true
    })
    builder.addCase(getDistributeScoreChart.fulfilled, (state, { payload }) => {
      state.isLoading.distributionScore = false
      state.distributionScoreData = convertData(payload)
    })
    builder.addCase(getDistributeScoreChart.rejected, (state, action) => {
      state.isLoading.distributionScore = action.payload.loading
    })

    builder.addCase(getDistributeScoreCompareChart.pending, (state) => {
      state.isLoading.distributionScoreCompare = true
    })
    builder.addCase(
      getDistributeScoreCompareChart.fulfilled,
      (state, { payload }) => {
        state.isLoading.distributionScoreCompare = false
        const data = {
          compare: payload?.default,
        }
        state.distributionScoreDataCompare = convertData(data)
      },
    )
    builder.addCase(
      getDistributeScoreCompareChart.rejected,
      (state, action) => {
        state.isLoading.distributionScoreCompare = action.payload.loading
      },
    )

    builder.addCase(getCommentary.pending, (state) => {
      state.isLoading.commentary = true
    })
    builder.addCase(getCommentary.fulfilled, (state, { payload }) => {
      state.isLoading.commentary = false
      state.commentaryData = payload
    })
    builder.addCase(getCommentary.rejected, (state, action) => {
      state.isLoading.commentary = action.payload.loading
    })

    builder.addCase(getPerformanMatrixChartData.pending, (state) => {
      state.isLoading.matrixChart = true
    })
    builder.addCase(
      getPerformanMatrixChartData.fulfilled,
      (state, { payload }) => {
        state.isLoading.matrixChart = false
        state.matrixChart = payload
      },
    )
    builder.addCase(getPerformanMatrixChartData.rejected, (state, action) => {
      state.isLoading.matrixChart = action.payload.loading
    })

    builder.addCase(getPerformanMatrixDisplayData.pending, (state) => {
      state.isLoading.matrixTooltip = true
    })
    builder.addCase(
      getPerformanMatrixDisplayData.fulfilled,
      (state, { payload }) => {
        state.isLoading.matrixTooltip = false
        state.matrixTooltip = payload
      },
    )
    builder.addCase(getPerformanMatrixDisplayData.rejected, (state, action) => {
      state.isLoading.matrixTooltip = action.payload.loading
    })
  },
})

register(slice.name, slice.reducer)

export const selectLoading = (type) => (state) =>
  state[slice.name].isLoading[type]
export const selectGenericRatioHighlight = (state) =>
  state[slice.name].genericRatioHighlightData
export const selectFinancialRatiosIds = (state) => {
  return state[slice.name].financialRatiosIds
}
export const selectFinancialRatiosData = (state) => {
  return state[slice.name].financialRatiosData
}
export const selectFinancialRatiosRowGroup = (rowGroupId) => (state) =>
  state[slice.name].financialRatiosData[rowGroupId]
export const selectFinancialRatiosCellData = (rowId, index, colId) => (state) =>
  state[slice.name].financialRatiosData[rowId][index][colId]
export const selectActivitiesRatiosAndRiskIds = (state) => {
  return state[slice.name].activitiesRatiosAndRiskIds
}
export const selectActivitiesRatiosAndRiskData = (state) => {
  return state[slice.name].activitiesRatiosAndRiskData
}
export const selectActivitiesRatiosAndRiskRowGroup = (rowGroupId) => (state) =>
  state[slice.name].activitiesRatiosAndRiskData[rowGroupId]
export const selectActivitiesRatiosAndRiskCellData =
  (rowId, index, colId) => (state) =>
    state[slice.name].activitiesRatiosAndRiskData[rowId][index][colId]
export const selectDistributionScoreData = (state) => {
  const defaultData = state[slice.name].distributionScoreData
  const compareData = state[slice.name].distributionScoreDataCompare
  return mergeData(defaultData, compareData)
}
export const selectDistributionScoreDataCompare = (state) => {
  return state[slice.name].distributionScoreDataCompare
}
export const selectCommentaryData = (state) => {
  return state[slice.name].commentaryData
}
export const selectMatrixTooltip = (state) => {
  return state[slice.name].matrixTooltip
}
export const selectMatrixChart = (state) => {
  return state[slice.name].matrixChart
}

const convertData = (data) => {
  if (!data.default && !data.compare) return []

  const dataMap =
    data.default && data.default.length
      ? data.default
      : data.compare && data.compare.length
      ? data.compare
      : []

  return dataMap.map((item, index) => {
    const defaultItem = data.default?.[index]
    const compareItem = data.compare?.[index]
    return {
      defaultMedian: defaultItem?.median ?? null,
      dataFloatBarDefault: defaultItem
        ? [defaultItem.min, defaultItem.max]
        : null,
      compareMedian: compareItem?.median ?? null,
      dataFloatBarCompare: compareItem
        ? [compareItem.min, compareItem.max]
        : null,
      year: defaultItem?.year ?? compareItem?.year ?? null,
      maxValue: Math.max(
        defaultItem ? defaultItem.max : 0,
        compareItem ? compareItem.max : 0,
      ),
      minValue: Math.min(
        defaultItem ? defaultItem.min : 0,
        compareItem ? compareItem.min : 0,
      ),
    }
  })
}

const mergeData = (defaultData, compareData) => {
  if (!defaultData?.length && !compareData?.length) return []
  if (!defaultData?.length) return compareData
  if (!compareData?.length) return defaultData

  const dataMap = new Map()

  defaultData.forEach((item) => {
    dataMap.set(item.year, { ...item })
  })

  compareData.forEach((item) => {
    if (dataMap.has(item.year)) {
      const current = dataMap.get(item.year)
      dataMap.set(item.year, {
        ...current,
        compareMedian: item.compareMedian,
        dataFloatBarCompare: item.dataFloatBarCompare,
        maxValue: Math.max(current.maxValue, item.maxValue),
        minValue: Math.min(current.minValue, item.minValue),
      })
    } else {
      dataMap.set(item.year, { ...item })
    }
  })

  return Array.from(dataMap.values()).sort((a, b) => a.year - b.year)
}

const mergeGroupData = (currentData, newData) => {
  const map = new Map()
  currentData.forEach((item) => map.set(item.ratioName, item))
  newData.forEach((item) => {
    if (map.has(item.ratioName)) {
      map.set(item.ratioName, { ...map.get(item.ratioName), ...item })
    } else {
      map.set(item.ratioName, item)
    }
  })
  return Array.from(map.values())
}
